/** The itinerary record's card layout (plan §5 "Cards"): the registry and one itinerary → the
 *  seven cards in the order the app presents the itinerary (Catalog entry · App button · Days ·
 *  Content · Visibility & search · Access · Identity & source), each split into its Editable
 *  block, the muted "From Branch.io" block (the deep link and its QR code — made by Branch.io,
 *  read-only) and the System block. Pure — the cards' FieldSlots read the draft themselves.
 *
 *  The Days card is laid out like the others (its one field, the day membership), but drawn
 *  by its own component, not as a field grid (`DaysCard`). */
import type { ItineraryDetail } from '@/api/itineraries';
import { ITINERARY_RECORD_SECTIONS, type ItineraryRecordSection } from '@/api/itineraryRecord';
import type { FieldBlockKind, ViewBlock, ViewSection } from '@/features/records/views/types';

import {
  ITINERARY_ENTITY,
  ITINERARY_RECORD_REGISTRY,
  ITINERARY_SECTION_TITLES,
  itineraryPlacements,
  type ItineraryRegistry,
} from '../registry';

/** Where the deep link and its QR code come from. */
export const FROM_BRANCH = { label: 'From Branch.io', note: 'read-only, made in the background' } as const;

/** The DOM id of a card — what the number keys and the band's cells scroll to. */
export function itinerarySectionAnchorId(section: ItineraryRecordSection): string {
  return `itinerary-section-${section}`;
}

const BLOCK_ORDER: FieldBlockKind[] = ['editable', 'vendor', 'system'];

export function itinerarySections(
  d: Pick<ItineraryDetail, 'id'>,
  registry: ItineraryRegistry = ITINERARY_RECORD_REGISTRY,
): ViewSection<ItineraryRecordSection>[] {
  const placements = itineraryPlacements(registry);
  return ITINERARY_RECORD_SECTIONS.map((section) => {
    const keys = placements.get(section) ?? [];
    const blocks: ViewBlock[] = [];
    for (const kind of BLOCK_ORDER) {
      const fields = keys
        .filter((k) => registry[k].block === kind)
        .map((k) => ({ key: k, entity: ITINERARY_ENTITY, id: d.id, path: k }));
      if (fields.length === 0) continue;
      blocks.push({
        kind,
        ...(kind === 'vendor' ? { label: FROM_BRANCH.label, note: FROM_BRANCH.note } : kind === 'system' ? { label: 'System' } : {}),
        fields,
      });
    }
    return { id: section, title: ITINERARY_SECTION_TITLES[section], blocks };
  });
}
